import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import FormStruct from "@/components/Form/FormStruct/FormStruct";
import React, { useEffect, useState } from "react";
import { Field, FormRenderProps } from "react-final-form";
import VacancyContractPreview from "../contract/VacancyContractPreview";
import "./VacancyContractDepositForm.scss";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import { RentalUnit } from "@/apps/tatar/cashBudget/views/tenants/TenantsInterfaces";
import i18n from "@/i18n";
import VacancyContractDeposit from "./components/VacancyContractDeposit";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { FV } from "@/components/Form/Validation/FormValidators";
import BFDate from "@/modules/abstract-ui/forms/date/BFDate";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import classNames from "classnames";
import { getVacancyContractSignatureHTML } from "../contract/paragraphs/VacancyContractSignature";
import ReactDOMServer from "react-dom/server";
import StringUtils from "@/utils/StringUtils";
import VacancyContractCancelButton from '../VacancyContractCancelButton';
import _ from 'lodash';
import { VacancyContractFormType, VacancyContractDepositFormType } from '../../../../model/VacancyApplicant.interface';

type Props = {
  onSubmit: (values: any) => void;
  goBack?: () => void;
  object: OAObject;
  rentalUnit: RentalUnit;
  previousState: VacancyContractFormType;
  currentState: VacancyContractDepositFormType | undefined;
  replacements: any;
  activity: APActivity;
};

const VacancyContractDepositForm = (props: Props) => {
  const {
    onSubmit,
    goBack,
    object,
    previousState,
    rentalUnit,
    replacements,
    activity,
    currentState,
  } = props;

  const [currentFieldEditing, setCurrentFieldEditing] = useState<string | null>(
    null
  );
  const [initialValues, setInitialValues] = useState<
    VacancyContractDepositFormType | undefined
  >();
  const [templateReplacments, setTemplateReplacements] = useState<
    { [key: string]: string } | undefined
  >();
  const [letterTemplateId] = useState(previousState.letterTemplateId);

  useEffect(() => {
    if (!_.isEmpty(currentState)) {
      setInitialValues(currentState);
      return;
    }

    const depsositSum = Number(previousState?.rentalUnitRentBase || 1) * 3;

    const firstContractPerson = previousState.tenants[0];

    const initialState: VacancyContractDepositFormType = {
      depositSum: depsositSum,
      depositDate: previousState?.moveInDate,
      bankUsageReferenceDeposit: `Kaution, Objekt ${object.data.id}, ME ${
        rentalUnit.data.id
      }, Mieter ${firstContractPerson?.firstName || "-"} ${
        firstContractPerson?.lastName || "-"
      }`,
      templateContent: "",
      templateSuffix: getVacancyContractSignatureHTML({
        applicantCount: previousState.tenants.length,
      }),
      templatePrefix: "",
    };

    setInitialValues(initialState);
    // disabled because all values proposed here are covered by those ids
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object._id, activity._id, rentalUnit._id]);

  useEffect(() => {
    setTemplateReplacements({
      ...replacements,
      ...previousState,
    });
  }, [replacements, previousState]);

  return (
    <div className="vacancy-applicant-contract-form">
      <FormStruct
        additionalActions={<VacancyContractCancelButton />}
        initialValues={initialValues}
        onSubmit={(data: VacancyContractDepositFormType) => {
          const templateContent = getDepositHtml();
          const submitData: VacancyContractDepositFormType = {
            ...data,
            templateContent,
          };
          onSubmit(submitData);
        }}
        submitText={i18n.t("Global.Buttons.continue", "Weiter")}
        onAbort={goBack}
        cancelText={i18n.t("Global.Buttons.back")}
        abortBtnProps={{
          appearance: "outline",
          onClick: goBack,
        }}
        render={(formRenderProps: FormRenderProps<any>) => {
          return (
            <div className="contract-form-content __flex-row">
              <div className="contract-form-fields __flex-1">
                <div className="deposit-form-part form-section">
                  <div className="__h3">
                    {i18n.t(
                      "apTemplate:Vacancy.Applicant.Contract.Label.Deposit",
                      "Kautionszahlung"
                    )}
                  </div>
                  <div className={classNames("__flex-row __justify-between")}>
                    <Field name="depositDate">
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFDate
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.DepositDate",
                              "Zu Zahlen bis:"
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>

                    <Field name="depositSum">
                      {({ input, meta }) => (
                        <div className={`__field`}>
                          <BFInput
                            type="priceInput"
                            currencySuffix={StringUtils.getCurrencySymbol(
                              "EUR"
                            )}
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.DepositSum",
                              "Summe"
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                  <div className={classNames("__flex-row")}>
                    <Field name="bankUsageReferenceDeposit">
                      {({ input, meta }) => (
                        <div className={`__field __flex-1`}>
                          <BFInput
                            label={i18n.t(
                              "apTemplate:Vacancy.Applicant.Contract.Label.DepositUsageReference",
                              "Verwendungszweck"
                            )}
                            {...input}
                            {...FV.getValidation(meta)}
                            onFocus={() => {
                              setCurrentFieldEditing(input.name);
                              input.onFocus();
                            }}
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="contract-preview __flex-3">
                <FormFieldValues names={["templateSuffix", "templatePrefix"]}>
                  {([templateSuffix, templatePrefix]) => {
                    return (
                      <VacancyContractPreview
                        currentFieldEditing={currentFieldEditing}
                        resolvedVariables={{
                          ...formRenderProps.values,
                          ...templateReplacments,
                        }}
                        letterTemplateId={letterTemplateId}
                        contentNodes={
                          <>
                            <VacancyContractDeposit />
                          </>
                        }
                        prefixHtml={templatePrefix}
                        suffixHtml={templateSuffix}
                      />
                    );
                  }}
                </FormFieldValues>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};

export default VacancyContractDepositForm;

const getDepositHtml = () => {
  return ReactDOMServer.renderToString(
    <>
      <VacancyContractDeposit />
    </>
  );
};
